/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-base-to-string */
import React from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import { useSubjectLanguageVariants } from '@features/user';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';

import type { Patient, ProviderBillingInfo } from '../../../types/types';

export interface RequiredInfoFieldsData {
  // Provider fields
  fax_number?: string;
  npi_number?: string;
  communications_name?: string;
  signing_name?: string;

  // Patient fields
  dob?: string;
  email?: string;
}

export interface RequiredInfoFieldsProps {
  // Current data
  providerData?: Partial<ProviderBillingInfo>;
  patientData?: Partial<Patient>;

  // Which fields are missing/required
  missingFields: {
    provider: Array<
      keyof Pick<
        ProviderBillingInfo,
        'fax_number' | 'npi_number' | 'communications_name' | 'signing_name'
      >
    >;
    patient: Array<'dob' | 'email'>;
  };

  // Form control
  control: Control<any>; // From parent form
  errors?: FieldErrors<any>;

  // Loading states
  isLoadingProvider?: boolean;
  isLoadingPatient?: boolean;
}

export const RequiredInfoFields: React.FC<RequiredInfoFieldsProps> = ({
  missingFields,
  control,
  errors = {},
  isLoadingProvider = false,
  isLoadingPatient = false,
}) => {
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();

  const hasProviderFields = missingFields.provider.length > 0;
  const hasPatientFields = missingFields.patient.length > 0;

  if (!hasProviderFields && !hasPatientFields) {
    return null;
  }

  return (
    <Box>
      {/* Provider Fields Section */}
      {hasProviderFields && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Provider Information Required
          </Typography>

          {isLoadingProvider ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {missingFields.provider.includes('fax_number') && (
                <FormControl fullWidth error={!!errors.fax_number}>
                  <Controller
                    name="fax_number"
                    control={control}
                    render={({ field }) => (
                      <MuiTelInput
                        {...field}
                        label="Fax Number *"
                        size="small"
                        defaultCountry="US"
                        forceCallingCode
                        onlyCountries={['US', 'CA']}
                        helperText="Your fax number for receiving lab results"
                        error={!!errors.fax_number}
                      />
                    )}
                  />
                  {errors.fax_number?.message && (
                    <FormHelperText error>{String(errors.fax_number.message)}</FormHelperText>
                  )}
                </FormControl>
              )}

              {missingFields.provider.includes('npi_number') && (
                <FormControl fullWidth error={!!errors.npi_number}>
                  <Controller
                    name="npi_number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="NPI Number *"
                        size="small"
                        placeholder="1234567890"
                        helperText="Your 10-digit National Provider Identifier"
                        error={!!errors.npi_number}
                        inputProps={{
                          maxLength: 10,
                          pattern: '[0-9]*',
                        }}
                      />
                    )}
                  />
                  {errors.npi_number?.message && (
                    <FormHelperText error>{String(errors.npi_number.message)}</FormHelperText>
                  )}
                </FormControl>
              )}

              {missingFields.provider.includes('communications_name') && (
                <FormControl fullWidth error={!!errors.communications_name}>
                  <Controller
                    name="communications_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Communications Name *"
                        size="small"
                        placeholder="Dr. Jane Smith"
                        helperText="Name used for lab communications (e.g., Dr. Jane Smith)"
                        error={!!errors.communications_name}
                      />
                    )}
                  />
                  {errors.communications_name?.message && (
                    <FormHelperText error>
                      {String(errors.communications_name.message)}
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              {missingFields.provider.includes('signing_name') && (
                <FormControl fullWidth error={!!errors.signing_name}>
                  <Controller
                    name="signing_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Signing Name *"
                        size="small"
                        placeholder="Jane Smith, MD"
                        helperText="Your name as it appears on signatures (e.g., Jane Smith, MD)"
                        error={!!errors.signing_name}
                      />
                    )}
                  />
                  {errors.signing_name?.message && (
                    <FormHelperText error>{String(errors.signing_name.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* Patient Fields Section */}
      {hasPatientFields && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {subjectLanguageCap} Information Required
          </Typography>

          {isLoadingPatient ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {missingFields.patient.includes('dob') && (
                <FormControl fullWidth error={!!errors.patient_dob}>
                  <Controller
                    name="patient_dob"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${subjectLanguageCap} Date of Birth *`}
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          max: new Date().toISOString().split('T')[0],
                        }}
                        helperText="Required for lab processing"
                        error={!!errors.patient_dob}
                      />
                    )}
                  />
                  {errors.patient_dob?.message && (
                    <FormHelperText error>{String(errors.patient_dob.message)}</FormHelperText>
                  )}
                </FormControl>
              )}

              {missingFields.patient.includes('email') && (
                <FormControl fullWidth error={!!errors.patient_email}>
                  <Controller
                    name="patient_email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${subjectLanguageCap} Email`}
                        type="email"
                        size="small"
                        placeholder="patient@example.com"
                        error={!!errors.patient_email}
                      />
                    )}
                  />
                  <FormHelperText>
                    {errors.patient_email?.message ? (
                      <span style={{ color: 'error' }}>{String(errors.patient_email.message)}</span>
                    ) : (
                      <>
                        Optional for PDF generation • <strong>Required to send via email</strong>
                      </>
                    )}
                  </FormHelperText>
                </FormControl>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
